import clsx from 'clsx'
import {
  LINUX_CLASSIFICATIONS,
  REBOOT_SETTINGS,
  WINDOWS_CLASSIFICATIONS,
  type RebootSetting,
} from '@/types/patching'
import type { CampaignFormValues, DraftKey, ScopeDrafts } from '@/utils/patchValidation'
import { Field, FieldErrorList, TagInput, type ErrorsFor } from './FormParts'
import styles from './Patching.module.css'

interface ScopeFieldsProps {
  values: CampaignFormValues
  onChange: (patch: Partial<CampaignFormValues>) => void
  drafts: ScopeDrafts
  onDraftChange: (key: DraftKey, value: string) => void
  errorsFor: ErrorsFor
}

function toggle<T extends string>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

/** Patch scope inputs — enum values are exactly the contract §2 sets. */
export function ScopeFields({ values, onChange, drafts, onDraftChange, errorsFor }: ScopeFieldsProps) {
  const path = 'patch_scope.'
  return (
    <div className={styles.stack}>
      <fieldset className={styles.field} style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className={styles.label}>Windows classifications</legend>
        <div className={styles.checkboxGrid}>
          {WINDOWS_CLASSIFICATIONS.map((c) => (
            <label key={c} className={styles.checkboxRow}>
              <input
                type="checkbox"
                aria-label={`Windows ${c}`}
                checked={values.windows.includes(c)}
                onChange={() => onChange({ windows: toggle(values.windows, c) })}
              />
              <span>{c}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field} style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className={styles.label}>Linux classifications</legend>
        <div className={styles.checkboxGrid}>
          {LINUX_CLASSIFICATIONS.map((c) => (
            <label key={`linux-${c}`} className={styles.checkboxRow}>
              <input
                type="checkbox"
                aria-label={`Linux ${c}`}
                checked={values.linux.includes(c)}
                onChange={() => onChange({ linux: toggle(values.linux, c) })}
              />
              <span>{c}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <FieldErrorList messages={errorsFor(`${path}classifications`)} />

      <div className={styles.factGrid}>
        <TagInput
          id="kb-include"
          label="KB numbers to include"
          values={values.kb_include}
          onChange={(kb_include) => onChange({ kb_include })}
          draft={drafts.kb_include}
          onDraftChange={(v) => onDraftChange('kb_include', v)}
          placeholder="KB5000001"
          upperCase
          errors={errorsFor(`${path}kb_numbers_to_include`)}
        />
        <TagInput
          id="kb-exclude"
          label="KB numbers to exclude"
          values={values.kb_exclude}
          onChange={(kb_exclude) => onChange({ kb_exclude })}
          draft={drafts.kb_exclude}
          onDraftChange={(v) => onDraftChange('kb_exclude', v)}
          placeholder="KB5000002"
          upperCase
          errors={errorsFor(`${path}kb_numbers_to_exclude`)}
        />
        <TagInput
          id="mask-include"
          label="Package name masks to include"
          values={values.mask_include}
          onChange={(mask_include) => onChange({ mask_include })}
          draft={drafts.mask_include}
          onDraftChange={(v) => onDraftChange('mask_include', v)}
          placeholder="openssl*"
          hint="Use * as the wildcard."
          errors={errorsFor(`${path}package_name_masks_to_include`)}
        />
        <TagInput
          id="mask-exclude"
          label="Package name masks to exclude"
          values={values.mask_exclude}
          onChange={(mask_exclude) => onChange({ mask_exclude })}
          draft={drafts.mask_exclude}
          onDraftChange={(v) => onDraftChange('mask_exclude', v)}
          placeholder="kernel*"
          errors={errorsFor(`${path}package_name_masks_to_exclude`)}
        />
      </div>

      <div className={styles.factGrid}>
        <Field
          id="max-publish-date"
          label="Max patch publish date"
          hint="Only patches published on or before this date. Leave empty for no limit."
          errors={errorsFor(`${path}max_patch_publish_date`)}
        >
          <input
            id="max-publish-date"
            type="date"
            className={clsx(styles.input, errorsFor(`${path}max_patch_publish_date`).length > 0 && styles.inputError)}
            value={values.max_patch_publish_date}
            onChange={(e) => onChange({ max_patch_publish_date: e.target.value })}
          />
        </Field>
        <Field id="reboot-setting" label="Reboot setting" errors={errorsFor(`${path}reboot_setting`)}>
          <select
            id="reboot-setting"
            className={styles.select}
            value={values.reboot_setting}
            onChange={(e) => onChange({ reboot_setting: e.target.value as RebootSetting })}
          >
            {REBOOT_SETTINGS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <TagInput
        id="applications"
        label="Applications"
        values={values.applications}
        onChange={(applications) => onChange({ applications })}
        draft={drafts.applications}
        onDraftChange={(v) => onDraftChange('applications', v)}
        placeholder="ross-erp"
        hint="Application catalogue names. Applications without patch automation are reported as unsupported."
        errors={errorsFor(`${path}applications`)}
      />
    </div>
  )
}
